import React, { useState, useEffect } from 'react';
import { DashboardContent } from '@/components/dashboard';
import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';
import { useTenantApi } from '@/hooks';
import { useItemCodingExplorerFilters } from './hooks/useItemCodingExplorerFilters';
import { FilterPanel } from './components/FilterPanel';
import './ItemCodingExplorer.scss';
import { useUser } from '@/contexts/UserContext';

const ItemCodingExplorer: React.FC = () => {
    const { getFilteredReport } = useTenantApi();
    const { defaultRunConfig } = useUser();
    const {
        filters,
        appliedFilters,
        filterOptions,
        filterOptionsLoading,
        handleFilterChange,
        handleApplyFilters,
    } = useItemCodingExplorerFilters(defaultRunConfig);

    const [embedUrl, setEmbedUrl] = useState<string | undefined>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);

    const toggleFiltersExpanded = () => {
        setIsFiltersExpanded(!isFiltersExpanded);
    };

    // Construct filter payload from applied filters
    const constructFilterPayload = () => ({
        reportConfig: {
            name: 'item_coding_explorer',
            productLevel: Array.isArray(appliedFilters.productLevel)
                ? appliedFilters.productLevel[0]
                : appliedFilters.productLevel,
            categories: appliedFilters.categories,
            brands: appliedFilters.brands,
            manufacturers: appliedFilters.manufacturers,
            parentCompany: appliedFilters.parentCompanies,
            upcs: appliedFilters.upcs.map((ppg) => ppg.substring(0, 12)?.trim()),
            characteristics: appliedFilters.productDimensions,
        },
    });

    // Fetch Looker report URL on component mount and when filters are applied
    useEffect(() => {
        let timeoutId: number | undefined;
        let isMounted = true;
        const fetchReport = async () => {
            try {
                setLoading(true);
                setEmbedUrl('');
                setError(null);
                const payload = constructFilterPayload();
                const response = await getFilteredReport(null, payload);
                if (!isMounted) {
                    return;
                }
                
                // Check if the response indicates failure
                if (response.status === 'error' || !response.url) {
                    const errorMessage = response.message || 'Failed to load report. Please try again.';
                    setError(errorMessage);
                    setEmbedUrl('');
                    setLoading(false);
                    return;
                }
                
                setEmbedUrl(response.url);
                // Adding delay to reset loader to handle the gap of looker embed iframe render
                timeoutId = window.setTimeout(() => {
                    if (isMounted) {
                        setLoading(false);
                    }
                }, 2500);
            } catch (error) {
                console.error('Error fetching report:', error);
                if (isMounted) {
                    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
                    setError(errorMessage);
                    setLoading(false);
                    setEmbedUrl('');
                }
            }
        };
        fetchReport();
        return () => {
            isMounted = false;
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        };
    }, [refreshKey]);

    const handleApplyClick = () => {
        handleApplyFilters();
        setRefreshKey((prev) => prev + 1);
    };

    return (
        <DashboardContent title={'Item Coding Explorer'}>
            <ByzzerMask show={filterOptionsLoading} loading={filterOptionsLoading} />
            <div className="item-coding-explorer">
                <div className="item-coding-explorer__filter-header">
                    <button
                        type="button"
                        className={`item-coding-explorer__filter-toggle ${isFiltersExpanded ? 'open' : 'closed'}`}
                        onClick={toggleFiltersExpanded}
                    >
                        {isFiltersExpanded ? 'Collapse' : 'Expand'}
                    </button>
                </div>
                <FilterPanel
                    expanded={isFiltersExpanded}
                    filters={filters}
                    filterOptions={filterOptions}
                    filterOptionsLoading={filterOptionsLoading}
                    onFilterChange={handleFilterChange}
                    onApply={handleApplyClick}
                />
            </div>
            <div className="looker-report-container">
                <ByzzerMask show={loading} loading={loading} />
                {error && (
                    <div className="item-coding-explorer__error">
                        <p>{error}</p>
                        <button onClick={() => {
                            setError(null);
                            setRefreshKey((prev) => prev + 1);
                        }}>
                            Retry
                        </button>
                    </div>
                )}
                {embedUrl && !error && (
                    <iframe
                        key={refreshKey}
                        src={embedUrl}
                        className="looker-report-iframe"
                        title="Item Coding Explorer Report"
                    />
                )}
            </div>
        </DashboardContent>
    );
};

export default ItemCodingExplorer;
